import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

// Theme-aligned palette — matches HeedifyTheme.css chart colors
const COLORS = ['#00A4A6', '#1B3A6B', '#8B5CF6', '#F97316', '#EF4444', '#06B6D4', '#10b981', '#FFC700'];

const DonutChartWidget = ({ data, config }) => {
    const { t } = useTranslation();
    const nameKey       = config?.config?.nameKey       || config?.nameKey       || 'label';
    const valueKey      = config?.config?.valueKey      || config?.valueKey      || 'value';
    const categoryLabels = config?.config?.categoryLabels || config?.categoryLabels || {};
    const categoryColors = config?.config?.categoryColors || config?.categoryColors || {};

    const translatedData = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) return [];
        return data.map((item, index) => {
            const categoryKey  = item[nameKey] || item.label || item.name;
            const displayLabel = categoryLabels[categoryKey] || item.label || categoryKey;
            const displayColor = categoryColors[categoryKey] || item.color || COLORS[index % COLORS.length];
            return {
                ...item,
                name: displayLabel,
                displayValue: item[valueKey] || 0,
                color: displayColor
            };
        });
    }, [data, nameKey, valueKey, categoryLabels, categoryColors]);

    const total = useMemo(() =>
        translatedData.reduce((sum, e) => sum + (e.displayValue || 0), 0),
    [translatedData]);

    // Largest segment — used for center text color
    const dominant = useMemo(() =>
        translatedData.reduce((max, e) => (e.displayValue > (max?.displayValue ?? 0) ? e : max), null),
    [translatedData]);

    const dominantPct = total > 0 && dominant
        ? Math.round((dominant.displayValue / total) * 100)
        : 0;

    if (!Array.isArray(data) || data.length === 0) return null;

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Donut area — relative so the center overlay can track its real center */}
            <div style={{ flex: 1, position: 'relative', minHeight: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={translatedData}
                            dataKey="displayValue"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius="55%"
                            outerRadius="80%"
                            cornerRadius={3}
                            paddingAngle={1}
                            labelLine={false}
                        >
                            {translatedData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(value) => [`${Math.round((value / total) * 100)}% (${value})`, '']}
                            contentStyle={{ fontSize: 13, borderColor: '#e1e1e1', borderRadius: 6 }}
                        />
                    </PieChart>
                </ResponsiveContainer>

                {/* Center overlay — now matches true donut center since legend is outside */}
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none',
                    lineHeight: 1.2,
                    width: '45%',   // hole is ~55% radius → ~ring-free width for text
                    maxWidth: 140
                }}>
                    <div style={{
                        fontSize: 'clamp(18px, 5vw, 28px)',
                        fontWeight: 700,
                        color: dominant?.color || '#1B3A6B',
                    }}>
                        {dominantPct}%
                    </div>
                    <div style={{
                        fontSize: 11,
                        color: '#605E5C',
                        marginTop: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {dominant?.name || ''}
                    </div>
                </div>
            </div>

            {/* Legend — separate from chart so the text overlay can center correctly */}
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '6px 16px',
                paddingTop: 8,
                flexShrink: 0
            }}>
                {translatedData.map((entry, i) => (
                    <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 13,
                        color: '#323130',
                        whiteSpace: 'nowrap'
                    }}>
                        <span style={{
                            display: 'inline-block',
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            background: entry.color,
                            flexShrink: 0
                        }} />
                        {entry.name}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default DonutChartWidget;
